import {Button} from '@wandb/weave/components/Button';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import {formatTooltipValue, truncateText} from '../../../../charts/format';
import {PLOT_GRID_COLOR, PLOT_HEIGHT} from '../../ecpConstants';

const CONSTANTS = {
  OPACITY: 0.2,
  STROKE_WIDTH: 2,
  NAME_MAX_LEN: 20,
  DOMAIN: [0, 1] as [number, number],
  TOOLTIP_OFFSET_X: 8,
  TOOLTIP_OFFSET_Y: 32,
  // Config based on display mode
  NORMAL: {
    FONT_SIZE: 12,
    TOOLTIP_WIDTH: 230,
    MARGIN: {top: 16, right: 24, bottom: 16, left: 24},
    RADIUS: '90%',
    TOOLTIP_PAD_X: 12,
    TOOLTIP_PAD_Y: 8,
    COLOR_BOX: 'w-12 h-12',
    GAP: 'gap-8',
  },
  FULLSCREEN: {
    FONT_SIZE: 16, // ~12 * 1.33
    TOOLTIP_WIDTH: 420,
    MARGIN: {top: 32, right: 48, bottom: 32, left: 48},
    RADIUS: '100%',
    TOOLTIP_PAD_X: 16,
    TOOLTIP_PAD_Y: 12,
    COLOR_BOX: 'w-16 h-16',
    GAP: 'gap-12',
  },
};

export type MetricMetadata = {
  scoreType: 'binary' | 'continuous';
  unit?: string;
};
export type Series = {
  metrics: Record<string, number>;
  originalMetrics?: Record<string, number>; // Store original values for tooltips
  name: string;
  color: string;
};
export type RadarPlotData = Record<string, Series> & {
  metricMetadata?: Record<string, MetricMetadata>;
};

const extractLabel = (label: string) => label.split('.').pop() ?? label;

const CustomTick: React.FC<any> = ({
  payload,
  x,
  y,
  textAnchor,
  isFullscreen,
}) => {
  const config = isFullscreen ? CONSTANTS.FULLSCREEN : CONSTANTS.NORMAL;
  return (
    <text
      x={x}
      y={y}
      textAnchor={textAnchor}
      fill="#333"
      fontSize={config.FONT_SIZE}>
      <tspan x={x} dy="0.355em">
        {extractLabel(payload.value)}
      </tspan>
    </text>
  );
};

const RadarTooltip = ({
  active,
  payload,
  label,
  position,
  align,
  seriesInfo,
  metricMetadata,
  isFullscreen,
}: any) => {
  if (!active || !payload?.length) return null;

  const config = isFullscreen ? CONSTANTS.FULLSCREEN : CONSTANTS.NORMAL;
  const metricName =
    (typeof label === 'string' ? label : payload[0]?.payload?.metricName) ?? '';
  // Split metric name on first dot for cleaner display
  const dotIndex = metricName.indexOf('.');
  const nameParts =
    dotIndex === -1
      ? [metricName]
      : [metricName.slice(0, dotIndex), metricName.slice(dotIndex + 1)];

  const style: React.CSSProperties = {
    left: position?.x,
    top: position?.y,
    width: config.TOOLTIP_WIDTH,
    transform: `translate(${align.h === 'right' ? '-100%' : '0'}, ${
      align.v === 'bottom' ? '-100%' : '0'
    })`,
  };

  return (
    <Tailwind>
      <div
        className={`fixed pointer-events-none shadow-lg rounded bg-[#333] text-white z-50 ${
          isFullscreen ? 'text-base' : 'text-xs'
        }`}
        style={{
          ...style,
          padding: `${config.TOOLTIP_PAD_Y}px ${config.TOOLTIP_PAD_X}px`,
        }}>
        <div className={`font-bold ${isFullscreen ? 'mb-8' : 'mb-6'}`}>
          {nameParts.map((part, i) => (
            <div
              key={i}
              className={`overflow-hidden text-ellipsis whitespace-nowrap ${
                i > 0 ? 'font-mono text-moon-300' : ''
              }`}>
              {part}
            </div>
          ))}
        </div>
        {seriesInfo.map((s: any) => {
          // Try to get original value first, fall back to normalized value
          const originalKey = `${s.id}_original`;
          const originalVal = payload.find((p: any) => p.dataKey === s.id)
            ?.payload?.[originalKey];
          const normalizedVal = payload.find(
            (p: any) => p.dataKey === s.id
          )?.value;
          const val = originalVal !== undefined ? originalVal : normalizedVal;
          const meta = metricMetadata?.[metricName];
          const formattedValue =
            typeof val === 'number'
              ? formatTooltipValue(
                  val,
                  meta?.scoreType === 'binary' ? '%' : meta?.unit
                )
              : (val ?? '');

          return (
            <div
              key={s.id}
              className={`flex items-center mb-2 ${config.GAP} ${
                isFullscreen ? 'mb-3' : 'mb-2'
              }`}>
              <div
                className={`rounded-sm shrink-0 ${config.COLOR_BOX}`}
                style={{backgroundColor: s.color}}
              />
              <div className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                {truncateText(s.name, CONSTANTS.NAME_MAX_LEN)}:
              </div>
              <div className="font-bold font-mono shrink-0">
                {formattedValue}
              </div>
            </div>
          );
        })}
      </div>
    </Tailwind>
  );
};

const RechartsRadarChartCore: React.FC<{
  height: number;
  data: RadarPlotData;
  isFullscreen?: boolean;
}> = ({height, data, isFullscreen = false}) => {
  const config = isFullscreen ? CONSTANTS.FULLSCREEN : CONSTANTS.NORMAL;

  const [tooltipState, setTooltipState] = useState<{
    pos: {x: number; y: number};
    align: {h: 'left' | 'right'; v: 'top' | 'bottom'};
  } | null>(null);

  const {chartData, seriesInfo, metadata} = useMemo(() => {
    const {metricMetadata, ...seriesData} = data;
    const entries = Object.entries(seriesData) as [string, Series][];

    if (!entries.length) {
      return {chartData: [], seriesInfo: [], metadata: metricMetadata};
    }

    const metricNames = Object.keys(entries[0][1].metrics);
    const chartData = metricNames.map(metricName => {
      const datum: {
        metricName: string;
        [key: string]: string | number;
      } = {
        metricName,
      };
      entries.forEach(([id, s]) => {
        // Use normalized values for plotting
        datum[id] = s.metrics[metricName] ?? 0;
        // Store original values for tooltips with _original suffix
        if (s.originalMetrics) {
          datum[`${id}_original`] = s.originalMetrics[metricName] ?? 0;
        }
      });
      return datum;
    });

    const seriesInfo = entries.map(([id, s]) => ({
      id,
      name: s.name,
      color: s.color,
    }));
    return {chartData, seriesInfo, metadata: metricMetadata};
  }, [data]);

  const handleMouseMove = useCallback(
    (_: any, e: React.SyntheticEvent) => {
      const mouseEvent = e as unknown as React.MouseEvent;
      const svg = (mouseEvent.target as Element)?.closest('svg');
      if (!svg) return;

      const rect = svg.getBoundingClientRect();

      // In fullscreen mode, always keep tooltip on the left
      // In normal mode, flip based on cursor position to avoid occlusion
      const isLeft = mouseEvent.clientX - rect.left < rect.width / 2;
      const newState = {
        pos: {
          x: isFullscreen
            ? rect.left + CONSTANTS.TOOLTIP_OFFSET_X
            : isLeft
              ? rect.right - CONSTANTS.TOOLTIP_OFFSET_X
              : rect.left + CONSTANTS.TOOLTIP_OFFSET_X,
          y: rect.top + CONSTANTS.TOOLTIP_OFFSET_Y,
        },
        align: {
          h: (isFullscreen ? 'left' : isLeft ? 'right' : 'left') as
            | 'left'
            | 'right',
          v: 'top' as 'top' | 'bottom',
        },
      };

      setTooltipState(prev =>
        prev?.pos.x === newState.pos.x && prev.align.h === newState.align.h
          ? prev
          : newState
      );
    },
    [isFullscreen]
  );

  if (!seriesInfo.length) return <div>No data available</div>;

  return (
    <>
      <style>{`.recharts-wrapper * { outline: none !important; }`}</style>
      <ResponsiveContainer width="100%" height={height}>
        <RadarChart
          outerRadius={config.RADIUS}
          data={chartData}
          margin={config.MARGIN}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setTooltipState(null)}>
          <PolarGrid stroke={PLOT_GRID_COLOR} />
          <PolarAngleAxis
            dataKey="metricName"
            tick={(props: any) => (
              <CustomTick {...props} isFullscreen={isFullscreen} />
            )}
          />
          <PolarRadiusAxis
            domain={CONSTANTS.DOMAIN}
            tick={false}
            axisLine={false}
          />
          {seriesInfo.map(s => (
            <Radar
              key={s.id}
              name={s.name}
              dataKey={s.id}
              stroke={s.color}
              fill={s.color}
              fillOpacity={CONSTANTS.OPACITY}
              strokeWidth={CONSTANTS.STROKE_WIDTH}
              isAnimationActive={false}
            />
          ))}
          <Tooltip
            wrapperClassName="pointer-events-none"
            isAnimationActive={false}
            content={(props: any) => (
              <RadarTooltip
                {...props}
                position={tooltipState?.pos}
                align={tooltipState?.align ?? {h: 'left', v: 'top'}}
                seriesInfo={seriesInfo}
                metricMetadata={metadata}
                isFullscreen={isFullscreen}
              />
            )}
          />
        </RadarChart>
      </ResponsiveContainer>
    </>
  );
};

export const RechartsRadarChart: React.FC<{
  data: RadarPlotData;
  withFullscreen?: boolean;
}> = ({data, withFullscreen = false}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!isFullscreen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isFullscreen]);

  if (!withFullscreen) {
    return <RechartsRadarChartCore height={PLOT_HEIGHT} data={data} />;
  }

  return (
    <Tailwind>
      <div
        className="relative flex items-center justify-center border border-moon-250 rounded-lg"
        style={{
          height: PLOT_HEIGHT,
          width: PLOT_HEIGHT * 1.8,
          flex: '1 0 auto',
        }}>
        <RechartsRadarChartCore height={PLOT_HEIGHT} data={data} />
        <Button
          variant="ghost"
          size="small"
          icon="full-screen-mode-expand"
          onClick={() => setIsFullscreen(true)}
          className="absolute top-1 right-1 min-w-0 w-32 h-32 p-0"
        />
      </div>

      {isFullscreen && (
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 9999}}
          onClick={() => setIsFullscreen(false)}>
          <div
            className="bg-white shadow-2xl flex flex-col overflow-hidden w-[90vw] max-w-[90vw] mt-[120px] rounded-xl"
            style={{
              height: 'calc(90vh - 120px)',
              maxHeight: 'calc(90vh - 120px)',
            }}
            onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 z-20 h-64 w-full border-b border-moon-250 flex items-center justify-between px-24 bg-white">
              <div className="text-xl font-semibold">Metrics Radar Chart</div>
              <button
                onClick={() => setIsFullscreen(false)}
                className="text-moon-500 hover:text-moon-800 transition-colors p-8 -m-8">
                <Icon name="close" />
              </button>
            </div>
            <div className="flex-1 flex items-center justify-center p-32">
              <RechartsRadarChartCore
                height={Math.min(window.innerHeight * 0.7, 700)}
                data={data}
                isFullscreen={true}
              />
            </div>
          </div>
        </div>
      )}
    </Tailwind>
  );
};
